import { useInquiryModal } from '@/components/inquiry/InquiryModal';
import { Link } from '@inertiajs/react';

export interface AuthorHeroData {
    name: string;
    avatar: string | null;
    bio: string | null;
    profile_url: string | null;
    is_agent: boolean;
    agent_id: number | null;
    agent_slug: string | null;
    influencer_slug?: string | null;
}

interface AuthorHeroProps {
    author: AuthorHeroData;
    /** Inquiry modal context — used when the CTA button fires. */
    articleId?: number;
    articleSlug?: string;
    /** When true, the author name is a link to their profile page. Default true. */
    nameLinksToProfile?: boolean;
    /** When true, the name and avatar link to the author's canonical profile outside the
     *  journal — agent page (/the-team/{slug}) or influencer page (/{slug}). Agent wins
     *  when both exist. No-op when neither slug is present. */
    linkToExternalProfile?: boolean;
    /** When true, renders the border-top separator above the section. */
    withTopBorder?: boolean;
    /** Optional kicker override. Defaults to "Plan your next journey with" (agent) or "About the author" (non-agent). */
    kicker?: string;
    /** 'vertical' (default): centered column for use as an in-article closing section.
     *  'horizontal': avatar on left, content column on right — for dedicated author pages. */
    layout?: 'vertical' | 'horizontal';
    /** Override the "Read more from …" link target. Used on interview articles, where
     *  the CTA features the interview subject but the "Read more" credits the writer. */
    readMoreOverride?: { name: string; href: string | null };
}

export function AuthorHero({
    author,
    articleId,
    articleSlug,
    nameLinksToProfile = true,
    linkToExternalProfile = false,
    withTopBorder = true,
    kicker,
    layout = 'vertical',
    readMoreOverride,
}: AuthorHeroProps) {
    const { openInquiry } = useInquiryModal();
    const firstName = author.name.split(' ')[0];
    const defaultKicker = author.is_agent ? 'Plan your next journey with' : 'About the author';
    const effectiveKicker = kicker ?? defaultKicker;

    const externalProfileUrl = linkToExternalProfile
        ? author.agent_slug
            ? `/the-team/${author.agent_slug}`
            : author.influencer_slug
              ? `/${author.influencer_slug}`
              : null
        : null;

    const handlePlanTrip = () => {
        if (!author.is_agent || !author.agent_id) return;
        openInquiry({
            source: 'journal_article_bottom',
            agent_id: author.agent_id,
            agent_name: firstName,
            agent_photo: author.avatar || undefined,
            agent_slug: author.agent_slug || undefined,
            ...(articleId ? { article_id: articleId } : {}),
            ...(articleSlug ? { article_slug: articleSlug } : {}),
            page_url: window.location.href,
        });
    };

    const nameClass = 'block text-2xl font-normal md:text-3xl';
    const nameStyle = { fontFamily: "'Didot', 'Playfair Display', Georgia, serif", color: '#0e2727' };

    const kickerEl = (
        <p className="mb-3 text-[11px] font-medium tracking-[0.3em] uppercase" style={{ color: 'rgba(14, 39, 39, 0.45)' }}>
            {effectiveKicker}
        </p>
    );

    const nameLinkHref = externalProfileUrl ?? (nameLinksToProfile ? author.profile_url : null);
    const nameEl = nameLinkHref ? (
        <Link href={nameLinkHref} className={`${nameClass} transition-colors hover:opacity-70`} style={nameStyle}>
            {author.name}
        </Link>
    ) : (
        <h3 className={nameClass} style={nameStyle}>
            {author.name}
        </h3>
    );

    const bioEl = author.bio ? (
        <p className={`mt-4 text-sm leading-relaxed ${layout === 'vertical' ? 'mx-auto max-w-lg' : ''}`} style={{ color: 'rgba(14, 39, 39, 0.6)' }}>
            {author.bio}
        </p>
    ) : null;

    const ctaEl =
        author.is_agent && author.agent_id ? (
            <button
                onClick={handlePlanTrip}
                className="mt-7 inline-flex cursor-pointer items-center gap-2 rounded-sm border px-6 py-2 text-[11px] font-medium tracking-[0.25em] uppercase transition-colors"
                style={{ borderColor: '#0b2222', backgroundColor: '#0b2222', color: '#F5F5F5' }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0e2727';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0b2222';
                }}
            >
                Plan a trip with {firstName}
                <span aria-hidden="true">&rarr;</span>
            </button>
        ) : null;

    const readMoreName = (readMoreOverride?.name ?? author.name).split(' ')[0];
    const readMoreHref = readMoreOverride ? readMoreOverride.href : nameLinksToProfile ? author.profile_url : null;
    const readMoreEl = readMoreHref ? (
        <div className="mt-5">
            <Link
                href={readMoreHref}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase transition-colors hover:opacity-70"
                style={{ color: 'rgba(14, 39, 39, 0.55)' }}
            >
                Read more from {readMoreName}
                <span aria-hidden="true">&rarr;</span>
            </Link>
        </div>
    ) : null;

    const avatarImg = author.avatar ? (
        <img src={author.avatar} alt={author.name} className="h-24 w-24 rounded-full object-cover" />
    ) : null;

    if (layout === 'horizontal') {
        const horizontalAvatar = avatarImg
            ? externalProfileUrl
                ? (
                      <Link href={externalProfileUrl} className="flex-shrink-0 transition-opacity hover:opacity-80">
                          {avatarImg}
                      </Link>
                  )
                : <span className="flex-shrink-0">{avatarImg}</span>
            : null;

        return (
            <div className="mx-auto max-w-3xl px-6">
                <div
                    className={`${withTopBorder ? 'border-t' : ''} py-14`}
                    style={withTopBorder ? { borderColor: 'rgba(14, 39, 39, 0.1)' } : undefined}
                >
                    <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-start sm:gap-8 sm:text-left">
                        {horizontalAvatar}
                        <div className="min-w-0 flex-1">
                            {kickerEl}
                            {nameEl}
                            {bioEl}
                            {ctaEl}
                            {readMoreEl}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const verticalAvatar = avatarImg
        ? externalProfileUrl
            ? (
                  <Link href={externalProfileUrl} className="mx-auto mb-6 inline-block transition-opacity hover:opacity-80">
                      {avatarImg}
                  </Link>
              )
            : <div className="mx-auto mb-6 inline-block">{avatarImg}</div>
        : null;

    return (
        <div className="mx-auto max-w-3xl px-6">
            <div
                className={`${withTopBorder ? 'border-t' : ''} py-14 text-center`}
                style={withTopBorder ? { borderColor: 'rgba(14, 39, 39, 0.1)' } : undefined}
            >
                {verticalAvatar}
                {kickerEl}
                {nameEl}
                {bioEl}
                {ctaEl}
                {readMoreEl}
            </div>
        </div>
    );
}
